import { useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { displayDate } from '@/lib/dates';
import { formatMoney } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { useOrgId } from '@/lib/org';
import {
  isTypingTarget,
  moveActive,
  toSections,
  useDebounced,
  useGlobalSearch,
  type SearchResult,
} from '@/lib/search';
import { homeCurrency } from '@/lib/org-locale';

/**
 * Global search in the top bar (document 05): transaction numbers, amounts, contacts, memos,
 * accounts and products. `/` or Ctrl+Alt+F focuses it; recent transactions while it is empty.
 */
export function GlobalSearch() {
  const orgId = useOrgId();
  const router = useRouter();
  const href = useRouterState({ select: (s) => s.location.href });
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debounced = useDebounced(text, 250);
  const search = useGlobalSearch(debounced, open && Boolean(orgId));
  const { sections, flat } = toSections(search.data);
  const shownQ = search.data?.q ?? '';

  const focus = () => {
    if (!orgId) return;
    inputRef.current?.focus();
    inputRef.current?.select();
    setOpen(true);
  };
  useHotkeys({ 'ctrl+alt+f': focus });
  // `/` opens search unless the user is typing in another field
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      if (!inputRef.current || inputRef.current.disabled) return;
      e.preventDefault();
      inputRef.current.focus();
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // close on navigation and when the client changes
  useEffect(() => {
    setOpen(false);
  }, [href, orgId]);

  // highlight the first result whenever the results change
  const resultKey = flat.map((r) => r.id).join();
  useEffect(() => {
    setActive(resultKey ? 0 : -1);
  }, [resultKey]);

  useEffect(() => {
    if (active < 0) return;
    panelRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const go = (r: SearchResult) => {
    setOpen(false);
    inputRef.current?.blur();
    void router.navigate({ href: r.href });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => moveActive(i, e.key === 'ArrowDown' ? 1 : -1, flat.length));
    } else if (e.key === 'Enter') {
      const r = flat[active];
      if (open && r) {
        e.preventDefault();
        go(r);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (open) setOpen(false);
      else inputRef.current?.blur();
    } else if (!open) {
      setOpen(true);
    }
  };

  const onClick = (e: MouseEvent<HTMLAnchorElement>, r: SearchResult) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    go(r);
  };

  const debouncing = text.trim() !== debounced.trim();
  const status = !orgId
    ? null
    : search.isError
      ? search.error.message
      : search.isPending || (search.isFetching && !flat.length)
        ? 'Searching…'
        : !flat.length && !debouncing
          ? shownQ
            ? `No results for “${shownQ}”`
            : 'No recent transactions'
          : null;

  return (
    <div
      className="relative order-last w-full lg:order-none lg:ml-auto lg:w-[320px]"
      title={orgId ? undefined : 'Select a client first'}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
      <Input
        ref={inputRef}
        id="global-search"
        type="search"
        role="combobox"
        aria-label="Search"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        autoComplete="off"
        className="pl-8"
        placeholder="Search transactions, contacts, accounts"
        disabled={!orgId}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && orgId ? (
        <div
          ref={panelRef}
          className="absolute right-0 top-full z-50 mt-1 max-h-[70vh] w-full overflow-y-auto lg:w-[520px] rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          data-testid="global-search-results"
          // keep focus in the input while clicking inside the panel
          onMouseDown={(e) => e.preventDefault()}
        >
          <div id={listId} role="listbox" aria-label="Search results">
            {sections.map((s) => (
              <div key={s.key} role="group" aria-label={s.label} className="py-1">
                <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {s.label}
                </div>
                {s.rows.map(({ index, result: r }) => (
                  <a
                    key={`${r.type}-${r.id}`}
                    id={`${listId}-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={index === active}
                    href={r.href}
                    tabIndex={-1}
                    onClick={(e) => onClick(e, r)}
                    onMouseMove={() => setActive(index)}
                    className={`flex items-start justify-between gap-3 rounded-sm px-2 py-1.5 text-sm ${
                      index === active ? 'bg-accent text-accent-foreground' : ''
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="truncate font-medium">{r.title}</span>
                        {r.inactive ? <Badge variant="outline">Inactive</Badge> : null}
                      </span>
                      {r.subtitle ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {r.subtitle}
                        </span>
                      ) : null}
                    </span>
                    {r.amount !== undefined || r.date ? (
                      <span className="shrink-0 text-right">
                        {r.amount !== undefined ? (
                          <span className="tnum block">
                            {formatMoney(r.amount, r.currency ?? homeCurrency())}
                          </span>
                        ) : null}
                        {r.date ? (
                          <span className="tnum block text-xs text-muted-foreground">
                            {displayDate(r.date)}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                  </a>
                ))}
              </div>
            ))}
          </div>
          {status ? <p className="px-2 py-3 text-sm text-muted-foreground">{status}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
